import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { cn } from '../../lib/utils';
import { useTheme } from '../../contexts/ThemeContext';
import { API_ENDPOINTS } from '../../config/apiConfig';
import {
  getDateFormat,
  getTableActionsConfig,
  TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX,
} from '../../config/appConfig';
import { CheckCircleFilledIcon, XCircleFilledIcon, FileIcon, ArrowUpIcon } from '../ui/Icons';
import { normalizeFileName } from '../ui/utils/fileNameEncoding';
import { CELL_RENDERERS } from './tableCellRenderers';

// Density
const DENSITY_CONFIG = {
  compact: { rowHeight: 'h-9', padding: 'px-3 py-2', fontSize: 'text-xs' },
  normal: { rowHeight: 'h-12', padding: 'px-4 py-3', fontSize: 'text-sm' },
  comfortable: { rowHeight: 'h-14', padding: 'px-5 py-4', fontSize: 'text-base' }
};

// A modern, glassy table with pill headers, rounded rows and subtle elevation
// Keeps sorting, drag reorder, sizing persistence; lighter resize visuals.
const ModernTable = ({
  columns,
  data,
  density = 'normal',
  tableId,
  className = '',
  customSortOrders = {},
  externalColumnWidths = {},
  externalColumnOrder = [],
  externalSortConfig = [],
  onConfigChange,
  onFileCountClick,
  onTodoTaskCountClick
}) => {
  const { theme, isDark } = useTheme();
  
  // Get table actions configuration
  const actionsVisibility = getTableActionsConfig('visibility');
  const showTooltips = getTableActionsConfig('showTooltips');
  const buttonSize = getTableActionsConfig('buttonSize');
  const spacing = getTableActionsConfig('spacing');
  const [columnWidths, setColumnWidths] = useState({});
  const [columnOrder, setColumnOrder] = useState(columns.map(col => col.key));
  const [sortConfig, setSortConfig] = useState([]);
  const [isResizing, setIsResizing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [draggedColumn, setDraggedColumn] = useState(null);
  const [dragOverColumn, setDragOverColumn] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const tableRef = useRef(null);
  const lastExternalSortRef = useRef(null);

  // Load
  useEffect(() => {
    const saved = localStorage.getItem(`modern-table-config-${tableId}`);
    if (saved) {
      try {
        const { widths, order, sortConfig: savedSort } = JSON.parse(saved);
        if (widths) setColumnWidths(widths);
        if (order && order.length === columns.length) setColumnOrder(order);
        if (savedSort) setSortConfig(savedSort);
      } catch {}
    }
    setIsInitialized(true);
  }, [tableId, columns.length]);

  const saveConfiguration = useCallback(() => {
    const cfg = { widths: columnWidths, order: columnOrder, sortConfig };
    localStorage.setItem(`modern-table-config-${tableId}`, JSON.stringify(cfg));
  }, [columnWidths, columnOrder, sortConfig, tableId]);

  useEffect(() => {
    if (isInitialized && !isResizing && externalColumnWidths && JSON.stringify(externalColumnWidths) !== JSON.stringify(columnWidths)) {
      setColumnWidths(externalColumnWidths);
    }
  }, [isInitialized, isResizing, externalColumnWidths, columnWidths]);

  useEffect(() => {
    if (isInitialized && !isDragging && externalColumnOrder && externalColumnOrder.length > 0 && JSON.stringify(externalColumnOrder) !== JSON.stringify(columnOrder)) {
      setColumnOrder(externalColumnOrder);
    }
  }, [isInitialized, isDragging, externalColumnOrder, columnOrder]);

  useEffect(() => {
    if (!isInitialized) return;
    const next = externalSortConfig ?? [];
    const serialized = JSON.stringify(next);
    if (lastExternalSortRef.current === serialized) return;
    lastExternalSortRef.current = serialized;
    setSortConfig(next);
  }, [isInitialized, externalSortConfig]);

  // Notify parent debounced
  useEffect(() => {
    if (isInitialized && !isResizing && !isDragging && onConfigChange) {
      const t = setTimeout(() => onConfigChange({ columnWidths, columnOrder, sortConfig }), 200);
      return () => clearTimeout(t);
    }
  }, [isInitialized, isResizing, isDragging, columnWidths, columnOrder, sortConfig, onConfigChange]);

  useEffect(() => {
    const t = setTimeout(saveConfiguration, 500);
    return () => clearTimeout(t);
  }, [saveConfiguration]);

  // Resize
  const handleMouseDown = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    setIsResizing(columnKey);
    const startX = e.pageX;
    const currentWidth = columnWidths[columnKey];
    const startWidth = currentWidth ? (typeof currentWidth === 'string' ? parseInt(currentWidth) : currentWidth) : 160;
    const handleMouseMove = (e) => {
      const diff = e.pageX - startX;
      const newWidth = Math.max(100, startWidth + diff);
      setColumnWidths(prev => ({ ...prev, [columnKey]: `${newWidth}px` }));
    };
    const handleMouseUp = () => {
      setIsResizing(false);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      setTimeout(() => {
        if (onConfigChange) onConfigChange({ columnWidths, columnOrder, sortConfig });
      }, 200);
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  };

  // Drag reorder
  const handleDragStart = (e, columnKey) => {
    if (e.target.classList.contains('resize-handle') || e.target.closest('.resize-handle')) {
      e.preventDefault();
      return false;
    }
    setIsDragging(true);
    setDraggedColumn(columnKey);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', columnKey);
  };
  const handleDragOver = (e, columnKey) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedColumn && draggedColumn !== columnKey) {
      setDragOverColumn(columnKey);
      e.dataTransfer.dropEffect = 'move';
    }
  };
  const handleDrop = (e, targetColumnKey) => {
    e.preventDefault();
    e.stopPropagation();
    if (draggedColumn && draggedColumn !== targetColumnKey) {
      const newOrder = [...columnOrder];
      const draggedIndex = newOrder.indexOf(draggedColumn);
      const targetIndex = newOrder.indexOf(targetColumnKey);
      if (draggedIndex !== -1 && targetIndex !== -1) {
        newOrder.splice(draggedIndex, 1);
        newOrder.splice(targetIndex, 0, draggedColumn);
        setColumnOrder(newOrder);
        setTimeout(() => onConfigChange && onConfigChange({ columnWidths, columnOrder: newOrder, sortConfig }), 200);
      }
    }
    setIsDragging(false);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };
  const handleDragEnd = () => {
    setIsDragging(false);
    setDraggedColumn(null);
    setDragOverColumn(null);
  };

  // Sorting
  const getCustomSortValue = useCallback((value, columnKey) => {
    const customOrder = customSortOrders[columnKey];
    if (customOrder) {
      const idx = customOrder.indexOf(value);
      return idx >= 0 ? idx : customOrder.length;
    }
    return value;
  }, [customSortOrders]);

  const handleSort = (columnKey, event) => {
    const isMulti = event.ctrlKey || event.metaKey;
    setSortConfig(prev => {
      if (!isMulti) {
        const existing = prev.find(s => s.key === columnKey);
        if (existing) return existing.direction === 'asc' ? [{ key: columnKey, direction: 'desc', priority: 1 }] : [];
        return [{ key: columnKey, direction: 'asc', priority: 1 }];
      } else {
        const idx = prev.findIndex(s => s.key === columnKey);
        if (idx >= 0) {
          const existing = prev[idx];
          if (existing.direction === 'asc') {
            const copy = [...prev];
            copy[idx] = { ...existing, direction: 'desc' };
            return copy;
          }
          return prev.filter(s => s.key !== columnKey).map((s, i) => ({ ...s, priority: i + 1 }));
        }
        return [...prev, { key: columnKey, direction: 'asc', priority: prev.length + 1 }];
      }
    });
  };

  const resolveRowSortValue = useCallback(
    (row, sortKey) => {
      const column = columns.find((col) => col.key === sortKey);
      const dataKey = column?.displayField || sortKey;
      let value = row[dataKey];
      if (value === undefined && dataKey !== sortKey) value = row[sortKey];
      if (
        value &&
        typeof value === 'object' &&
        !Array.isArray(value) &&
        !(value instanceof Date)
      ) {
        value =
          value.name ??
          value.title ??
          value.label ??
          value.userDisplayName ??
          value.typeName ??
          value.statusName ??
          value.code ??
          value.id;
      }
      return getCustomSortValue(value, sortKey);
    },
    [columns, getCustomSortValue]
  );

  const sortedData = useMemo(() => {
    if (sortConfig.length === 0) return data;
    return [...data].sort((a, b) => {
      for (const sort of sortConfig.sort((a, b) => a.priority - b.priority)) {
        const aVal = resolveRowSortValue(a, sort.key);
        const bVal = resolveRowSortValue(b, sort.key);
        let cmp = 0;
        if (aVal == null && bVal == null) continue;
        if (aVal == null) return 1;
        if (bVal == null) return -1;
        if (typeof aVal === 'string' && typeof bVal === 'string') cmp = aVal.localeCompare(bVal, undefined, { numeric: true });
        else if (typeof aVal === 'number' && typeof bVal === 'number') cmp = aVal - bVal;
        else if (aVal instanceof Date && bVal instanceof Date) cmp = aVal.getTime() - bVal.getTime();
        else cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
        if (cmp !== 0) return sort.direction === 'asc' ? cmp : -cmp;
      }
      return 0;
    });
  }, [data, sortConfig, resolveRowSortValue]);

  const densityConfig = DENSITY_CONFIG[density] || DENSITY_CONFIG.normal;
  const orderedColumns = columnOrder.map(key => columns.find(col => col.key === key)).filter(Boolean);
  const useStickyActionsColumn = sortedData.length > 0;

  const widthForColumn = (column) => {
    if (column.key === 'actions') {
      const raw = column.width || '120px';
      const px = parseInt(String(raw), 10) || 120;
      return `${Math.max(px, TABLE_ACTIONS_COLUMN_MIN_WIDTH_PX)}px`;
    }
    return columnWidths[column.key] || column.width || '160px';
  };

  const colWidthStyle = (column, index, columnCount) => {
    if (column.key === 'actions') {
      const w = widthForColumn(column);
      return { width: w, maxWidth: w };
    }
    if (index === columnCount - 1) {
      return { width: 'auto' };
    }
    return { width: widthForColumn(column) };
  };

  const cellLayoutStyle = (column) => ({
    width: widthForColumn(column),
    minWidth: widthForColumn(column),
    ...(column.key === 'actions' && useStickyActionsColumn ? { left: 0 } : {}),
  });

  const actionsStickyClass = () =>
    useStickyActionsColumn ? 'sticky-actions-column z-20' : null;

  const formatDateByPattern = (date, pattern) => {
    try {
      const d = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(d.getTime())) return '';
      const dd = String(d.getDate()).padStart(2, '0');
      const MM = String(d.getMonth() + 1).padStart(2, '0');
      const yyyy = String(d.getFullYear());
      return (pattern || '').replace('dd', dd).replace('MM', MM).replace('yyyy', yyyy) || d.toLocaleDateString();
    } catch {
      return '';
    }
  };

  const renderCell = (column, row) => {
    if (column.key === 'actions') {
      const actionsElement = row[column.key];
      // If it's a React element, apply configuration
      if (React.isValidElement(actionsElement)) {
        return React.cloneElement(actionsElement, {
          className: cn(
            actionsElement.props.className,
            spacing,
            actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
          )
        });
      }
      return actionsElement;
    }
    let value = row[column.key];
    if (column.displayField && row[column.displayField] !== undefined) {
      value = row[column.displayField];
    }

    if (typeof column.render === 'function') {
      const custom = column.render(value, row, column);
      if (custom == null || custom === '') {
        return <span className={theme.text.muted}>—</span>;
      }
      if (React.isValidElement(custom)) {
        return custom;
      }
      return <span className={`text-sm ${theme.text.secondary}`}>{String(custom)}</span>;
    }
    
    // Early check: If value looks like an ISO date string, format it immediately
    // This catches cases where cellRenderer might not be set correctly
    if (value && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value)) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          // Use dateFormat from column config if available, otherwise use default format
          const pattern = column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
          const day = String(date.getDate()).padStart(2, '0');
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const year = date.getFullYear();
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const formatted = pattern
            .replace('yyyy', year)
            .replace('MM', month)
            .replace('dd', day)
            .replace('HH', hours)
            .replace('mm', minutes);
          return (
            <span className={`text-sm ${theme.text.secondary}`}>{formatted}</span>
          );
        }
      } catch (error) {
        // Fall through to cellRenderer checks
      }
    }
    
    const cellType = column.cellRenderer;
    
    // Helper function to format ISO date strings
    const formatDateTimeValue = (val, formatPattern) => {
      if (!val) return null;
      try {
        const date = new Date(val);
        if (isNaN(date.getTime())) return null;
        
        const pattern = formatPattern || column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        // Replace pattern placeholders (order matters: replace longer patterns first to avoid conflicts)
        let formatted = pattern
          .replace('yyyy', year)
          .replace('MM', month)
          .replace('dd', day)
          .replace('HH', hours)
          .replace('mm', minutes);
        
        return formatted;
      } catch (error) {
        return null;
      }
    };
    
    if (cellType === 'date') {
      const pattern = column.dateFormat || (getDateFormat && getDateFormat('table'));
      const formatted = value ? formatDateByPattern(value, pattern) : '';
      return (
        <span className={`text-sm ${theme.text.secondary}`}>{formatted || '—'}</span>
      );
    }
    if (cellType === 'dateTime' || cellType === 'datetime') {
      if (!value) return <span className={theme.text.muted}>—</span>;
      
      // Direct formatting for ISO date strings
      const dateStr = String(value);
      if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr)) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            const pattern = column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const formatted = pattern
              .replace('yyyy', year)
              .replace('MM', month)
              .replace('dd', day)
              .replace('HH', hours)
              .replace('mm', minutes);
            return (
              <span className={`text-sm ${theme.text.secondary}`}>{formatted}</span>
            );
          }
        } catch (error) {
          // Fall through
        }
      }
      
      // Try to format the value using the helper function
      const formatted = formatDateTimeValue(value);
      if (formatted) {
        return (
          <span className={`text-sm ${theme.text.secondary}`}>{formatted}</span>
        );
      }
      
      return <span className={theme.text.muted}>—</span>;
    }
    if (cellType === 'badge' && CELL_RENDERERS.badge) {
      return CELL_RENDERERS.badge(value, column, row, theme);
    }
    if (cellType === 'statusBadge' && CELL_RENDERERS.statusBadge) {
      return CELL_RENDERERS.statusBadge(value, column, row, theme);
    }
    if (cellType === 'booleanBadge' && CELL_RENDERERS.booleanBadge) {
      return CELL_RENDERERS.booleanBadge(value, column, row, theme);
    }
    if (cellType === 'priorityBadge' && CELL_RENDERERS.priorityBadge) {
      return CELL_RENDERERS.priorityBadge(value, column, row, theme);
    }
    if (cellType === 'color' && CELL_RENDERERS.color) {
      return CELL_RENDERERS.color(value, column, row, theme);
    }
    if (cellType === 'toggleSwitch') {
      const config = column.toggleConfig || {};
      const handleToggle = async () => {
        if (config.onToggle && window.todoHandlers && window.todoHandlers[config.onToggle]) {
          try {
            await window.todoHandlers[config.onToggle](row.id, value);
            // The handler manages the API call and data refresh
          } catch (error) {
            console.error('Toggle failed:', error);
          }
        }
      };
      
      return (
        <div className="flex items-center justify-center">
          <button
            onClick={handleToggle}
            className={cn(
              "relative inline-flex h-5 w-9 items-center rounded-full transition-colors duration-200",
              value ? 'bg-green-500' : 'bg-gray-300'
            )}
          >
            <span
              className={cn(
                "inline-block h-3 w-3 transform rounded-full bg-white transition-transform duration-200",
                value ? 'translate-x-5' : 'translate-x-1'
              )}
            />
          </button>
        </div>
      );
    }
    if (cellType === 'fileCount') {
      if (value === undefined || value === null) return <span className={theme.text.muted}>—</span>;
      return (
        <div className="flex justify-center items-center gap-1">
          <FileIcon className="w-4 h-4 text-blue-500" />
          <span 
            className={`text-sm font-medium ${theme.text.primary} cursor-pointer hover:text-blue-600`} 
            title={`${value} file(s) - Click to view/download`}
            onClick={() => {
              console.log('📁 File count clicked in ModernTable:', { value, row, hasHandler: !!onFileCountClick });
              if (onFileCountClick) {
                onFileCountClick(row, value);
              } else {
                console.log('❌ No onFileCountClick handler provided to ModernTable');
              }
            }}
          >
            {value}
          </span>
        </div>
      );
    }
    if (cellType === 'todoTaskOpenTotal') {
      let tasks = [];
      if (Array.isArray(value)) {
        tasks = value;
      } else if (typeof value === 'string' && value.trim()) {
        try {
          const parsed = JSON.parse(value);
          tasks = Array.isArray(parsed) ? parsed : [];
        } catch {
          tasks = [];
        }
      }

      const normalized = tasks
        .map((task) => ({
          task: String(task?.task ?? task?.Task ?? '').trim(),
          isDone: Boolean(task?.isDone ?? task?.IsDone),
        }))
        .filter((task) => task.task);

      const total = normalized.length;
      const open = normalized.filter((task) => !task.isDone).length;

      if (total === 0) {
        return <span className={theme.text.muted}>0/0</span>;
      }

      return (
        <button
          type="button"
          className={cn("text-sm font-medium underline decoration-dotted hover:opacity-80", theme.text.primary)}
          onClick={(event) => {
            event.stopPropagation();
            if (onTodoTaskCountClick) {
              onTodoTaskCountClick(row, normalized);
            }
          }}
          title="View tasks"
        >
          {open}/{total}
        </button>
      );
    }
    
    if (cellType === 'fileList') {
      if (value === undefined || value === null || value === 0) return <span className={theme.text.muted}>—</span>;
      
      // If we have the actual file data, display it
      if (row.fileAttachments && Array.isArray(row.fileAttachments)) {
        // Filter out deleted files
        const activeFiles = row.fileAttachments.filter(file => !file.isDeleted);
        return (
          <div className="space-y-1 max-h-32 overflow-y-auto">
            {activeFiles.map((file, index) => (
              <div key={index} className="flex items-center gap-2 p-1 bg-gray-50 dark:bg-gray-800 rounded text-xs">
                <FileIcon className="w-3 h-3 text-blue-500 flex-shrink-0" />
                <span className="truncate flex-1">{normalizeFileName(file.fileName || '')}</span>
                <a 
                  href={API_ENDPOINTS.fileAttachment.download(file.id)}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 underline text-xs flex-shrink-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  Download
                </a>
              </div>
            ))}
          </div>
        );
      }
      
      // Fallback to count if no file data
      return (
        <div className="flex justify-center items-center gap-1">
          <FileIcon className="w-4 h-4 text-blue-500" />
          <span className="text-sm font-medium">{value} file(s)</span>
        </div>
      );
    }
    // Fallback: Check if value is an ISO date string and format it (catches cases where cellRenderer wasn't set correctly)
    // This is a safety net for any ISO date strings that weren't handled by specific renderers
    if (value) {
      const dateStr = value instanceof Date ? value.toISOString() : String(value);
      // Check for ISO date format: YYYY-MM-DDTHH:mm:ss or YYYY-MM-DDTHH:mm:ss.sssZ
      const isISODate = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(dateStr);
      
      if (isISODate || value instanceof Date) {
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            // Use dateFormat from column config if available, otherwise use default format
            const pattern = column.dateFormat || (getDateFormat && getDateFormat('datetime')) || 'dd/MM/yyyy HH:mm';
            
            // Format based on pattern
            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();
            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            
            // Replace pattern placeholders (order matters: replace longer patterns first to avoid conflicts)
            let formatted = pattern
              .replace('yyyy', year)
              .replace('MM', month)
              .replace('dd', day)
              .replace('HH', hours)
              .replace('mm', minutes);
            
            return (
              <span className={`text-sm ${theme.text.secondary}`}>{formatted}</span>
            );
          }
        } catch (error) {
          // Fall through to default return
        }
      }
    }
    
    // Fallbacks for objects/arrays
    if (value && typeof value === 'object') {
      if (React.isValidElement(value)) return value;
      if (Array.isArray(value)) return value.length > 0 ? `[${value.length} items]` : '[]';
      const displayValue = value.name || value.title || value.label || value.typeName || value.statusName || (value.id ? `ID: ${value.id}` : '[Object]');
      return String(displayValue);
    }
    return value ?? '—';
  };

  return (
    <div className={cn(
      'relative h-full max-h-full min-w-0 w-full max-w-full rounded-2xl overflow-hidden',
      theme.colors.surface,
      'border border-white/10 shadow-[0_8px_30px_rgb(0,0,0,0.12)]',
      'dark:border-slate-700/50',
      className
    )}>
      <div 
        className="h-full min-w-0 w-full max-w-full overflow-x-auto overflow-y-auto advanced-table-scroll"
        style={{
          scrollbarWidth: 'thin',
          scrollbarColor: isDark ? '#6b7280 #374151' : '#d1d5db #f3f4f6',
        }}
      >
        <table ref={tableRef} className="w-full border-collapse" style={{ tableLayout: 'fixed' }}>
          <colgroup>
            {orderedColumns.map((column, index) => (
              <col
                key={`col-${column.key}`}
                style={colWidthStyle(column, index, orderedColumns.length)}
              />
            ))}
          </colgroup>
          {/* Header */}
          <thead className={cn(
            'sticky top-0 z-10 border-b',
            theme.border.light,
            theme.colors.surface,
          )}>
            <tr>
              {orderedColumns.map((column) => (
                <th
                  key={column.key}
                  draggable={column.key !== 'actions'}
                  onDragStart={column.key !== 'actions' ? (e) => handleDragStart(e, column.key) : undefined}
                  onDragOver={column.key !== 'actions' ? (e) => handleDragOver(e, column.key) : undefined}
                  onDrop={column.key !== 'actions' ? (e) => handleDrop(e, column.key) : undefined}
                  onDragEnd={column.key !== 'actions' ? handleDragEnd : undefined}
                  onDragLeave={column.key !== 'actions' ? () => setDragOverColumn(null) : undefined}
                  className={cn(
                    'relative select-none group transition-all duration-200',
                    densityConfig.padding,
                    'font-semibold text-sm',
                    column.key === 'actions'
                      ? cn(
                          'cursor-default',
                          actionsStickyClass(),
                          theme.colors.surface,
                          theme.colors.primary.replace('bg-', 'text-')
                        )
                      : 'cursor-move',
                    theme.text.primary,
                    dragOverColumn === column.key && `${theme.colors.accent} bg-opacity-20 ring-2 ring-current rounded-lg`,
                    draggedColumn === column.key && `opacity-70 ${theme.colors.background} transform scale-105`)
                  }
                  style={cellLayoutStyle(column)}
                >
                  <div className={column.key === 'actions' ? 'flex items-center justify-center px-2' : 'flex items-center justify-between pl-1'}>
                    {column.key === 'actions' ? (
                      <span className="sticky-actions-header-label whitespace-nowrap">{column.header}</span>
                    ) : (
                    <button
                      className={cn(
                        'inline-flex items-center gap-2 px-3 py-1 rounded-full transition-colors',
                        'bg-white/40 dark:bg-gray-700/30 hover:bg-white/60 dark:hover:bg-gray-700/50'
                      )}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleSort(column.key, e);
                      }}
                      title={`Sort by ${column.header}`}
                    >
                      <span className="truncate max-w-[180px]">{column.header}</span>
                      {(() => {
                        const s = sortConfig.find(x => x.key === column.key);
                        if (s) {
                          return (
                            <ArrowUpIcon className={cn('w-4 h-4 drop-shadow-sm transition-transform', s.direction === 'desc' && 'rotate-180')} />
                          );
                        }
                        return null;
                      })()}
                    </button>
                    )}

                    {column.key !== 'actions' && (
                      <div
                        className={cn(
                          'resize-handle absolute right-0 top-1/2 -translate-y-1/2 w-1.5 h-6 cursor-col-resize rounded',
                          'bg-transparent hover:bg-blue-500/70 transition-colors',
                          isResizing === column.key && 'bg-blue-500'
                        )}
                        onMouseDown={(e) => { e.stopPropagation(); handleMouseDown(e, column.key); }}
                        title={`Resize ${column.header}`}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>

          {/* Body */}
          <tbody>
            {sortedData.map((row, index) => (
              <tr key={index} className={cn('group transition-all duration-150')}> 
                {orderedColumns.map((column) => (
                <td
                    key={column.key}
                    className={cn(
                      'align-middle relative transition-all duration-150 font-normal',
                      densityConfig.padding,
                      theme.text.primary,
                    'border-b', theme.border.light,
                    column.key === 'actions'
                      ? cn(actionsStickyClass(), theme.colors.surface)
                      : (theme.colors.backgroundHover ||
                          (isDark ? 'bg-slate-800/50' : 'bg-slate-50')),
                    column.key === 'actions' && theme.colors.primary.replace('bg-', 'text-')
                    )}
                    style={cellLayoutStyle(column)}
                  >
                  <div className={column.key === 'actions' ? cn(
                    'flex justify-center',
                    spacing,
                    actionsVisibility === 'hover' ? 'opacity-0 group-hover:opacity-100 transition-opacity duration-200' : 'opacity-100'
                  ) : 'overflow-hidden text-ellipsis pl-2'}>
                    {renderCell(column, row)}
                  </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Overlay */}
      {isResizing && (
        <div className="absolute top-2 right-2 bg-black/70 text-white px-2 py-1 rounded text-xs">
          Resizing: {columnWidths[isResizing]}
        </div>
      )}
    </div>
  );
};

export default ModernTable;
export { ModernTable };


